import classNames from 'classnames';
import React, { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';

import {
  MAX_UPLOAD_FILE_SIZE_MB,
  MIN_WALLPAPER_HEIGHT,
  MIN_WALLPAPER_WIDTH,
} from '@wetransfer/transfer/component/TransferCustomizationProvider/constants';
import { useTransferCustomization } from '@wetransfer/transfer/component/TransferCustomizationProvider/hooks';
import { WallpaperCategory } from '@wetransfer/transfer/component/TransferCustomizationProvider/types';
import { Spinner } from '@wetransfer/transfer/spaces-bundle/components/Spinner/Spinner';
import { selectAuth0UserId } from '@wetransfer/transfer/store/selector/auth';
import { selectCountryCode } from '@wetransfer/transfer/store/selector/session';
import { selectUserPlanTier } from '@wetransfer/transfer/store/selector/user';

import { wallpaperInputOptions } from './constants';
import { useClickOutside } from './hooks/useClickOutside';
import useFileDialog from './hooks/useFileDialog';
import styles from './TransferCustomizationDrawer.module.scss';
import { getSurveyUrl, getWallpaperThumbnail } from './utils';

export const TransferCustomizationDrawer: React.FC = () => {
  const userId = useSelector(selectAuth0UserId);
  const subscription = useSelector(selectUserPlanTier) || 'anonymous';
  const country = useSelector(selectCountryCode);

  const {
    clearUploadedWallpaper,
    closeDrawer,
    isDrawerOpen,
    selectedWallpaperId,
    selectWallpaper,
    wallpapers,
    uploadCustomWallpaper,
    isUploadInProgress,
    uploadedWallpaperId,
    uploadExistingWallpaper,
    pendingWallpaperIds,
    completedWallpaperAssetIds,
  } = useTransferCustomization();

  const { openFileDialog } = useFileDialog(
    useCallback(
      (files: FileList) => uploadCustomWallpaper(files[0]),
      [uploadCustomWallpaper]
    ),
    wallpaperInputOptions
  );

  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(
    ref,
    useCallback(() => {
      if (isDrawerOpen) {
        closeDrawer();
      }
    }, [isDrawerOpen, closeDrawer])
  );

  const handleClearUpload = useCallback(async () => {
    /**
     * clearUploadedWallpaper causes the reset button to unmount, which useClickOutside treats
     * as a click outside the drawer. So, delay the call by a cycle.
     */
    await new Promise((resolve) => setTimeout(resolve, 0));
    clearUploadedWallpaper();
  }, [clearUploadedWallpaper]);

  const makeHandleThumbnailClick = useCallback(
    (category: WallpaperCategory, localId: string) => () => {
      if (
        pendingWallpaperIds.has(localId) ||
        completedWallpaperAssetIds.has(localId) ||
        (localId === uploadedWallpaperId && localId !== selectedWallpaperId)
      ) {
        selectWallpaper(localId);

        return;
      }

      if (
        category === WallpaperCategory.BRANDED ||
        category === WallpaperCategory.CURATED
      ) {
        uploadExistingWallpaper(localId);
      }
    },
    [
      completedWallpaperAssetIds,
      pendingWallpaperIds,
      selectWallpaper,
      selectedWallpaperId,
      uploadExistingWallpaper,
      uploadedWallpaperId,
    ]
  );

  return (
    <div
      className={classNames(styles.wrapper, isDrawerOpen && styles.open)}
      ref={ref}
    >
      <div className={styles.content}>
        <h2>Set custom wallpaper</h2>
        <p>
          Give this transfer its very own wallpaper to make a great impression
          when people download your files—don’t worry, this won’t change your{' '}
          <a href="/branding" onClick={closeDrawer}>
            default settings
          </a>
          .
        </p>
      </div>
      <ol className={styles.thumbnailGrid}>
        {wallpapers.map((wallpaper) => (
          <li
            className={classNames(
              styles.thumbnail,
              wallpaper.localId === selectedWallpaperId && styles.selected
            )}
            key={wallpaper.localId}
          >
            <button
              onClick={makeHandleThumbnailClick(
                wallpaper.category,
                wallpaper.localId
              )}
            >
              <img
                src={getWallpaperThumbnail(wallpaper)}
                alt={wallpaper.credits}
              />
              {isUploadInProgress &&
              uploadedWallpaperId === wallpaper.localId ? (
                <Spinner
                  className={styles.spinner}
                  circumference={16}
                  percentage={25}
                />
              ) : null}
            </button>
          </li>
        ))}
      </ol>
      <div className={styles.buttonRow}>
        <button
          className={styles.uploadButton}
          onClick={() => openFileDialog()}
          disabled={isUploadInProgress}
        >
          Upload
        </button>
        {selectedWallpaperId ? (
          <button className={styles.resetButton} onClick={handleClearUpload}>
            Reset to default
          </button>
        ) : (
          <span className={styles.fileRules}>
            JPEG/PNG {MIN_WALLPAPER_WIDTH}x{MIN_WALLPAPER_HEIGHT},{' '}
            {MAX_UPLOAD_FILE_SIZE_MB}MB max
          </span>
        )}
      </div>
      <a
        className={styles.feedbackButton}
        href={getSurveyUrl({
          subscription,
          userId,
          country,
        })}
        target="_blank"
        rel="noreferrer"
      >
        Let us know what you think
      </a>
    </div>
  );
};

TransferCustomizationDrawer.displayName = 'TransferCustomizationDrawer';
